import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { STATUSES, cn } from "@/lib/utils";
import { fetchSports, searchSports, sportIcon } from "@/lib/sportsCatalog";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Search, X, ChevronLeft, ChevronRight, Info, Check, CircleAlert } from "lucide-react";
import { useLang } from "@/i18n";
import { quotaMessage } from "@/lib/quotaMessages";

function SportCarousel({ sports, selectedId, onSelect }) {
  const { tf } = useLang();
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sports]);

  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 384, behavior: "smooth" });

  const onWheel = (e) => {
    const el = scrollRef.current;
    if (!el || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    el.scrollLeft += e.deltaY;
  };

  const onKeyDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    if (e.key === "Home") { e.preventDefault(); el.scrollTo({ left: 0, behavior: "smooth" }); }
    if (e.key === "End") { e.preventDefault(); el.scrollTo({ left: el.scrollWidth, behavior: "smooth" }); }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-[40px_minmax(0,1fr)_40px] items-center gap-2.5 min-w-0">
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        disabled={!canLeft}
        aria-label={tf("Sports précédents")}
        className="hidden sm:grid h-11 w-[38px] place-items-center rounded-[9px] border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/80 disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
        data-testid="sport-carousel-prev"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div
        ref={scrollRef}
        onScroll={updateArrows}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
        role="radiogroup"
        aria-label={tf("Choisir un sport")}
        className="sport-carousel min-w-0"
        data-testid="sport-carousel"
      >
        {sports.map((s) => {
          const Icon = sportIcon(s.id);
          const selected = s.id === selectedId;
          return (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={selected}
              data-selected={selected || undefined}
              title={s.label}
              onClick={() => onSelect(s.id)}
              className={cn(
                "shrink-0 basis-[86px] min-h-[70px] snap-start flex flex-col items-center justify-center gap-1.5 px-2 py-2.5 rounded-[10px] border transition-colors",
                selected
                  ? "border-athlynx/85 bg-athlynx/10 text-foreground"
                  : "border-border bg-white/[0.015] text-muted-foreground hover:bg-secondary/60 hover:border-foreground/25 hover:text-foreground"
              )}
              data-testid={`sport-option-${s.id}`}
            >
              <Icon className={cn("h-[22px] w-[22px]", selected && "text-athlynx")} />
              <span className="max-w-full text-xs font-medium leading-tight truncate">{s.label}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        disabled={!canRight}
        aria-label={tf("Sports suivants")}
        className="hidden sm:grid h-11 w-[38px] place-items-center rounded-[9px] border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/80 disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
        data-testid="sport-carousel-next"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function DynamicField({ field, value, onChange }) {
  const { tf } = useLang();
  const id = `athlete-${field.key}`;
  return (
    <div className={field.type === "text" && field.key === "position" ? "" : ""}>
      <Label htmlFor={id}>
        {tf(field.label)}
        {field.required ? <span className="text-athlynx ml-0.5" aria-hidden>*</span> : <span className="text-muted-foreground text-xs ml-1">{tf("(optionnel)")}</span>}
      </Label>
      {field.type === "select" ? (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger id={id} aria-required={field.required || undefined} data-testid={`athlete-field-${field.key}`}>
            <SelectValue placeholder={tf("Sélectionner")} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {(field.options || []).map((o) => (
              <SelectItem key={o} value={o}>{tf(o)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          type={field.type === "number" ? "number" : "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          aria-required={field.required || undefined}
          data-testid={`athlete-field-${field.key}`}
        />
      )}
    </div>
  );
}

function RequirementsPanel({ sports, selectedId, onSelect }) {
  const { tf } = useLang();
  const ordered = useMemo(() => {
    const sel = sports.find((s) => s.id === selectedId);
    const others = sports.filter((s) => s.id !== selectedId).slice(0, 6);
    return sel ? [sel, ...others] : others;
  }, [sports, selectedId]);

  return (
    <aside className="min-w-0" data-testid="sport-requirements-panel">
      <h3 className="text-base font-semibold">{tf("Ce qu'il faut selon le sport")}</h3>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">{tf("Les champs clés requis pour chaque sport.")}</p>
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {ordered.map((s) => {
          const Icon = sportIcon(s.id);
          const selected = s.id === selectedId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s.id)}
              aria-pressed={selected}
              className={cn(
                "w-full flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors",
                selected
                  ? "border-athlynx/70 bg-athlynx/[0.06]"
                  : "border-border bg-white/[0.01] hover:bg-secondary/50 hover:border-foreground/20"
              )}
              data-testid={`requirement-card-${s.id}`}
            >
              <div className={cn(
                "shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center",
                selected ? "border-athlynx/40 bg-athlynx/10 text-athlynx" : "border-border bg-secondary/60 text-muted-foreground"
              )}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{s.label}</div>
                <div className="text-[11px] text-muted-foreground leading-snug">{s.summary}</div>
              </div>
              {selected && (
                <span className="shrink-0 h-5 w-5 rounded-full bg-athlynx flex items-center justify-center" aria-hidden>
                  <Check className="h-3 w-3 text-white" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-border bg-secondary/30 px-3.5 py-3 text-[11px] text-muted-foreground">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>{tf("Les champs marqués d'un")} <span className="text-athlynx">*</span> {tf("sont obligatoires pour le sport sélectionné.")}</span>
      </div>
    </aside>
  );
}

export default function AddPlayerDialog({ open, onOpenChange, onCreated, defaultStatus = "Repéré" }) {
  const { tf, t } = useLang();
  const [sports, setSports] = useState(null);
  const [sportId, setSportId] = useState("football");
  const [search, setSearch] = useState("");
  const [common, setCommon] = useState({ name: "", age: "", status: defaultStatus, phone: "", email: "", club: "" });
  const [sportValues, setSportValues] = useState({});
  const [competition, setCompetition] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchSports().then(setSports).catch(() => {
      setSports([]);
      toast.error(tf("Impossible de charger le catalogue des sports"));
    });
  }, [open, tf]);

  const sport = useMemo(() => (sports || []).find((s) => s.id === sportId), [sports, sportId]);
  const filtered = useMemo(() => searchSports(sports || [], search), [sports, search]);

  const selectSport = (id) => {
    if (id === sportId) return;
    const hasSportValues = Object.values(sportValues).some((v) => v !== "" && v != null) || !!competition;
    if (hasSportValues && !window.confirm(tf("Changer de sport effacera les champs spécifiques déjà saisis. Continuer ?"))) return;
    setSportId(id);
    setSportValues({});
    setCompetition("");
  };

  const setSportValue = (key, v) => setSportValues((p) => ({ ...p, [key]: v }));

  const missing = useMemo(() => {
    const m = [];
    if (!common.name.trim()) m.push(tf("Nom complet"));
    if (!common.age) m.push(tf("Âge"));
    if (!sport) { m.push(tf("Sport")); return m; }
    sport.fields.forEach((f) => {
      if (f.required && !(sportValues[f.key] || "").toString().trim()) m.push(tf(f.label));
    });
    if (sport.club_required && !common.club.trim()) m.push(sport.club_label);
    if (sport.competition_required && !competition) m.push(sport.competition_label);
    if (!common.status) m.push(tf("Statut"));
    return m;
  }, [common, sport, sportValues, competition, tf]);

  const reset = () => {
    setCommon({ name: "", age: "", status: defaultStatus, phone: "", email: "", club: "" });
    setSportValues({});
    setCompetition("");
    setSearch("");
    setSportId("football");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (missing.length > 0 || saving || !sport) return;
    setSaving(true);
    try {
      const sd = {};
      sport.fields.forEach((f) => {
        const raw = sportValues[f.key];
        if (raw === "" || raw == null) return;
        sd[f.key] = f.type === "number" ? parseInt(raw, 10) : raw;
      });
      const position = sd.position || ""; delete sd.position;
      const foot = sd.foot || ""; delete sd.foot;
      const nationality = sd.nationality || ""; delete sd.nationality;
      const height = sd.height_cm; delete sd.height_cm;
      const payload = {
        name: common.name.trim(),
        age: common.age ? parseInt(common.age, 10) : null,
        status: common.status,
        phone: common.phone.trim(),
        email: common.email.trim(),
        club: common.club.trim(),
        sport: sport.id,
        competition,
        position,
        foot,
        nationality,
        height_cm: Number.isInteger(height) ? height : null,
        sport_data: sd,
      };
      const r = await api.post("/players", payload);
      toast.success(tf("Athlète ajouté"));
      onCreated?.(r.data);
      onOpenChange(false);
      reset();
    } catch (err) {
      if (err?.response?.status !== 402) {
        const qm = quotaMessage(err);
        const detail = err?.response?.data?.detail;
        toast.error(qm || (typeof detail === "string" ? detail : tf("Erreur lors de l'ajout")));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(1180px,calc(100vw-48px))] max-w-none max-h-[min(880px,calc(100vh-40px))] p-0 gap-0 flex flex-col overflow-hidden rounded-[18px] max-sm:w-screen max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none"
        data-testid="add-player-dialog"
      >
        {/* Header */}
        <div className="shrink-0 flex items-start justify-between gap-5 px-6 pt-[22px] pb-[18px] border-b border-border">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="shrink-0 h-11 w-11 rounded-xl bg-athlynx/12 border border-athlynx/30 flex items-center justify-center text-athlynx">
              <UserPlus className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-xl md:text-2xl font-semibold tracking-tight">{tf("Nouvel athlète")}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-0.5">
                {tf("Choisissez un sport puis complétez les champs adaptés.")}
              </DialogDescription>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col">
          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-6 items-start">
              {/* Left column */}
              <div className="min-w-0 space-y-4">
                <div>
                  <div className="text-base font-semibold mb-2.5">{tf("Sport")}</div>
                  {/* Sport search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={tf("Rechercher un sport…")}
                      aria-label={tf("Rechercher un sport")}
                      className="min-h-[44px] pl-10 pr-10 rounded-[10px] bg-white/[0.02]"
                      data-testid="sport-search-input"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label={tf("Effacer la recherche")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
                        data-testid="sport-search-clear"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Carousel */}
                  {!sports ? (
                    <div className="flex gap-2.5" aria-hidden>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="h-[70px] w-[86px] rounded-[10px] bg-secondary/50 animate-pulse shrink-0" />
                      ))}
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-dashed border-border px-4 py-5" data-testid="sport-empty">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CircleAlert className="h-4 w-4" /> {tf("Aucun sport trouvé")}
                      </div>
                      <Button type="button" size="sm" variant="outline" onClick={() => setSearch("")} data-testid="sport-empty-clear">
                        {tf("Effacer la recherche")}
                      </Button>
                    </div>
                  ) : (
                    <SportCarousel sports={filtered} selectedId={sportId} onSelect={selectSport} />
                  )}

                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Info className="h-3.5 w-3.5 shrink-0" />
                    {tf("Les champs du formulaire s'adaptent au sport sélectionné.")}
                  </div>
                </div>

                {/* Common fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">{tf("Nom complet")} <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Input
                      id="name"
                      value={common.name}
                      onChange={(e) => setCommon({ ...common, name: e.target.value })}
                      placeholder="ex: Lucas Tremblay"
                      data-testid="add-player-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="age">{tf("Âge")} <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Input
                      id="age"
                      type="number"
                      value={common.age}
                      onChange={(e) => setCommon({ ...common, age: e.target.value })}
                      placeholder="ex: 22"
                      data-testid="add-player-age-input"
                    />
                  </div>

                  {/* Sport-specific fields */}
                  {sport?.fields.map((f) => (
                    <DynamicField
                      key={`${sport.id}-${f.key}`}
                      field={f}
                      value={sportValues[f.key]}
                      onChange={(v) => setSportValue(f.key, v)}
                    />
                  ))}

                  {/* Club (per-sport label & requirement) */}
                  {sport && (
                    <div className="sm:col-span-2">
                      <Label htmlFor="club">
                        {tf(sport.club_label)}
                        {sport.club_required
                          ? <span className="text-athlynx ml-0.5" aria-hidden>*</span>
                          : <span className="text-muted-foreground text-xs ml-1">{tf("(optionnel)")}</span>}
                      </Label>
                      <Input
                        id="club"
                        value={common.club}
                        onChange={(e) => setCommon({ ...common, club: e.target.value })}
                        placeholder="ex: FC Versailles"
                        data-testid="add-player-club-input"
                      />
                    </div>
                  )}

                  {/* Competition + Statut */}
                  {sport && (
                    <div>
                      <Label>
                        {tf(sport.competition_label)}
                        {sport.competition_required && <span className="text-athlynx ml-0.5" aria-hidden>*</span>}
                      </Label>
                      <Select value={competition} onValueChange={setCompetition}>
                        <SelectTrigger data-testid="athlete-competition-trigger">
                          <SelectValue placeholder={tf("Sélectionner une ligue")} />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {sport.competitions.map((c) => (
                            <SelectItem key={c.id} value={c.id} data-testid={`competition-option-${c.id}`}>
                              {c.label}{c.country && c.country !== "France" ? ` · ${c.country}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>{tf("Statut")} <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Select value={common.status} onValueChange={(v) => setCommon({ ...common, status: v })}>
                      <SelectTrigger data-testid="add-player-status-trigger">
                        <SelectValue placeholder={tf("Sélectionner un statut")} />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="phone">{tf("Téléphone")} <span className="text-muted-foreground text-xs">{tf("(optionnel)")}</span></Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={common.phone}
                      onChange={(e) => setCommon({ ...common, phone: e.target.value })}
                      placeholder="ex: +33 6 12 34 56 78"
                      data-testid="add-player-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="player-email">Email <span className="text-muted-foreground text-xs">{tf("(optionnel)")}</span></Label>
                    <Input
                      id="player-email"
                      type="email"
                      value={common.email}
                      onChange={(e) => setCommon({ ...common, email: e.target.value })}
                      placeholder="ex: joueur@example.fr"
                      data-testid="add-player-email-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  {tf("Les ligues se mettent à jour selon le sport sélectionné.")}
                </div>
              </div>

              {/* Right column */}
              {sports && sports.length > 0 && (
                <RequirementsPanel sports={sports} selectedId={sportId} onSelect={selectSport} />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-card">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="add-player-cancel">
              {tf("Annuler")}
            </Button>
            <Button
              type="submit"
              disabled={saving || missing.length > 0}
              title={missing.length > 0 ? `${tf("Champs requis :")} ${missing.join(", ")}` : undefined}
              className="bg-athlynx text-white hover:bg-athlynx/90 min-w-[96px]"
              data-testid="add-player-submit"
            >
              {saving ? tf("Création…") : tf("Créer")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
